import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar, Filter, RefreshCw, Loader2, Clock, User,
  AlertTriangle, CheckCircle, XCircle, FileSpreadsheet, FileText,
  Building2, ChevronDown, DoorOpen, FileDown, Edit2, X, Save,
  Search, ArrowUp, ArrowDown, ArrowUpDown, Trash2
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { departmentService, employeeService } from '../services';
import { usePermission } from '../context/PermissionContext';
import { useAppContext } from '../context/AppContext';
import { Role } from '../types';
import SiteLocationFilter from '../components/SiteLocationFilter';
import api from '../services/api';
import { uyar, hata, onayla } from '../components/GlobalModal';

// Types
interface AttendanceLogRow {
  employeeId?: number;
  kartNo: string;
  isim: string;
  grup: string;
  bolum: string;
  tarih: string;
  giris1: string | null;
  cikis1: string | null;
  giris2: string | null;
  cikis2: string | null;
  giris3: string | null;
  cikis3: string | null;
  giris4: string | null;
  cikis4: string | null;
  aciklama: string;
  // 2026-08-14: O gun fiilen calisilan sure (backend hesapliyor)
  normalCalisma?: string | null;
  logIdIn1?: number | null;
  logIdOut1?: number | null;
  logIdIn2?: number | null;
  logIdOut2?: number | null;
  logIdIn3?: number | null;
  logIdOut3?: number | null;
  logIdIn4?: number | null;
  logIdOut4?: number | null;
}

interface AttendanceLogSummary {
  toplamPersonel: number;
  devamEden: number;
  devamsiz: number;
  cikisEksik: number;
}

interface AttendanceLogReport {
  filter: {
    baslangicTarihi: string;
    bitisTarihi: string;
    departmentId?: number;
    employeeId?: number;
    durum?: string;
  };
  rows: AttendanceLogRow[];
  summary: AttendanceLogSummary;
  generatedAt: string;
}

// Backend TimesheetApiController: [Route("api/[controller]")] = /api/TimesheetApi (case-insensitive)
const API_BASE = `${import.meta.env.VITE_API_URL || ''}/api/timesheetapi`;

const MONTH_KEYS = [
  'months.january', 'months.february', 'months.march', 'months.april',
  'months.may', 'months.june', 'months.july', 'months.august',
  'months.september', 'months.october', 'months.november', 'months.december'
];

const AttendanceLogs = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const urlEmployeeId = searchParams.get('employeeId');

  // State
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<AttendanceLogReport | null>(null);
  const [departments, setDepartments] = useState<{ id: number; name: string }[]>([]);
  const [employees, setEmployees] = useState<{ id: number; name: string; departmentId: number }[]>([]);
  const [employeeId, setEmployeeId] = useState<number | ''>(() => urlEmployeeId ? Number(urlEmployeeId) : '');
  const [autoLoadTriggered, setAutoLoadTriggered] = useState(false);

  // Admin yetki kontrolu — backend TimesheetApiController ile hizali:
  // UserID=1 daima admin VEYA role ADMIN VEYA rol resolve edilemedi (bilinmeyen custom rol).
  // ARS Ahsap raporu: "Sistem Yoneticisi (KULLANICI)" gibi ozel rol adlarinda resolveRole
  // MANAGER'a dusuyor, kalem gorunmuyordu. Backend TimesheetApiController zaten
  // roles.Any(r => r.Contains("admin") || r.StartsWith("sistem")) VE UserID=1 kontrolu
  // yapiyor — yani backend son karar mercii. Frontend'te MANAGER'a da UI edit butonu
  // goster, yetkisiz kullanici tiklarsa 403 hata alir (veri corrupted olmaz).
  // Sadece USER role (normal calisan) veya NFC_OPERATOR (kart okutucu) dislanir.
  const { user } = usePermission();
  const { currentUser } = useAppContext();

  // 2026-08-13 (Baglar talebi): Settings.Security.AttendanceEditRequireAdmin
  //   true  → SADECE Admin (veya UserID=1) duzenleyebilir — Manager/DeptManager/Personel HAYIR
  //   false → mevcut esnek davranis (Star Yapi/ARS/Lice)
  const [requireAdminForEdit, setRequireAdminForEdit] = useState<boolean | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const r: any = await api.get('/settings/Security');
        const raw = r?.data ?? r;
        const dict = raw?.data ?? raw ?? {};
        const v = dict?.AttendanceEditRequireAdmin;
        setRequireAdminForEdit(String(v ?? 'false').toLowerCase() === 'true');
      } catch {
        setRequireAdminForEdit(false);
      }
    })();
  }, []);

  const isStrictAdmin = (currentUser?.role === Role.ADMIN) || (user?.id === 1);
  const canEditTimes = requireAdminForEdit === null
    ? false  // setting yuklenene kadar butonu gosterme (yanlislikla izin verme)
    : requireAdminForEdit
      ? isStrictAdmin
      : (isStrictAdmin
        || currentUser?.role === Role.MANAGER
        || currentUser?.role === Role.DEPARTMENT_MANAGER);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRow, setEditingRow] = useState<AttendanceLogRow | null>(null);
  const [editGiris, setEditGiris] = useState('');
  const [editCikis, setEditCikis] = useState('');
  const [saving, setSaving] = useState(false);

  // 2026-08-15 (kullanici talebi): Tarayicinin siyah zeminli standart
  // confirm/alert kutusu hem kotu duruyor hem de kullanicilar okumadan
  // geciyordu. Uygulama ici, renk kodlu ve baslikli modal kullaniyoruz.
  type OnayTuru = 'onay' | 'uyari' | 'tehlike' | 'bilgi';
  const [onayKutusu, setOnayKutusu] = useState<{
    tur: OnayTuru;
    baslik: string;
    mesaj: string;
    satirlar?: { etiket: string; deger: string }[];
    notlar?: string[];
    onayMetni?: string;
    tekButon?: boolean;
    cevapla: (kabul: boolean) => void;
  } | null>(null);

  // await onayla() yerine: await sor({...}) -> true/false
  const sor = (cfg: {
    tur?: OnayTuru; baslik: string; mesaj: string;
    satirlar?: { etiket: string; deger: string }[];
    notlar?: string[]; onayMetni?: string;
  }) => new Promise<boolean>(cevapla => {
    setOnayKutusu({ tur: cfg.tur ?? 'onay', ...cfg, cevapla });
  });

  // uyar() yerine: await bildir({...})
  const bildir = (cfg: {
    tur?: OnayTuru; baslik: string; mesaj: string;
    satirlar?: { etiket: string; deger: string }[]; notlar?: string[];
  }) => new Promise<boolean>(cevapla => {
    setOnayKutusu({ tur: cfg.tur ?? 'bilgi', ...cfg, tekButon: true, cevapla });
  });

  // 2026-08-14 (kullanici): Hikvision cihazda yuz okutulduysa cekilen fotograf
  // veritabaninda tutuluyordu ama ARAYUZDE HICBIR YERDE gosterilmiyordu.
  // Duzenleme modalinda o gunun yuz kayitlari gorunur — operator kimin
  // okuttugunu gozle dogrulayabilir.
  const [yuzKayitlari, setYuzKayitlari] = useState<any[]>([]);
  const [yuzYukleniyor, setYuzYukleniyor] = useState(false);
  const [buyukFoto, setBuyukFoto] = useState<string | null>(null);

  // Card filter: 'all' | 'present' | 'absent' | 'exitMissing'
  const [activeFilter, setActiveFilter] = useState<'all' | 'present' | 'absent' | 'exitMissing'>('all');

  // Search and sort
  const [searchTerm, setSearchTerm] = useState('');
  type SortKey = 'kartNo' | 'isim' | 'bolum' | 'tarih' | 'giris1' | 'cikis1';
  type SortOrder = 'asc' | 'desc';
  const [sortKey, setSortKey] = useState<SortKey>('tarih');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Filters
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1); // Ayin 1'i
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [durum, setDurum] = useState('Tumu');
  const [siteId, setSiteId] = useState<string>('all');
  const [locationId, setLocationId] = useState<string>('all');

  // URL'den employeeId geldiyse otomatik rapor yukle
  useEffect(() => {
    if (urlEmployeeId && employees.length > 0 && !autoLoadTriggered) {
      setAutoLoadTriggered(true);
      loadReport();
    }
  }, [employees, urlEmployeeId, autoLoadTriggered]);

  // Load departments & employees
  useEffect(() => {
    loadDepartments();
    loadEmployees();
  }, []);

  const loadDepartments = async () => {
    try {
      const res = await departmentService.getAll();
      if (res.success && res.data) {
        setDepartments(res.data.map((d: any) => ({ id: d.id || d.departmentId, name: d.name || d.departmentName })));
      }
    } catch (error) {
      console.error('Departmanlar yuklenirken hata:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await employeeService.getAll(true);
      if (res.success && res.data) {
        setEmployees(res.data.map((e: any) => ({
          id: e.employeeId || e.id,
          name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
          departmentId: e.departmentId || e.departmentID || 0
        })));
      }
    } catch (error) {
      console.error('Personeller yuklenirken hata:', error);
    }
  };

  // Departman secildiginde personel filtreleme
  const filteredEmployees = departmentId
    ? employees.filter(e => e.departmentId === departmentId)
    : employees;

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        durum
      });
      if (departmentId) params.append('departmentId', departmentId.toString());
      if (employeeId) params.append('employeeId', employeeId.toString());
      if (siteId !== 'all') params.append('siteId', siteId);
      if (locationId !== 'all') params.append('locationId', locationId);

      const response = await fetch(`${API_BASE}/daily?${params}`);
      if (response.ok) {
        const data = await response.json();
        // Sadece giris/cikis verilerini al, puantaj hesaplamalarini cikart
        const simplifiedRows = data.rows.map((row: any) => ({
          employeeId: row.employeeId ?? row.employeeID ?? row.id,
          kartNo: row.kartNo,
          isim: row.isim,
          grup: row.grup,
          bolum: row.bolum,
          tarih: row.tarih,
          giris1: row.giris1, cikis1: row.cikis1,
          giris2: row.giris2, cikis2: row.cikis2,
          giris3: row.giris3, cikis3: row.cikis3,
          giris4: row.giris4, cikis4: row.cikis4,
          aciklama: row.aciklama,
          normalCalisma: row.normalCalisma ?? null,
          // Vardiya bilgisi (gece vardiyasi uyarisi icin) — backend Birim=VardiyaBaslangic, Firma=VardiyaBitis
          vardiya: row.vardiya ?? '',
          birim: row.birim ?? '',
          firma: row.firma ?? '',
          logIdIn1: row.logIdIn1 ?? null, logIdOut1: row.logIdOut1 ?? null,
          logIdIn2: row.logIdIn2 ?? null, logIdOut2: row.logIdOut2 ?? null,
          logIdIn3: row.logIdIn3 ?? null, logIdOut3: row.logIdOut3 ?? null,
          logIdIn4: row.logIdIn4 ?? null, logIdOut4: row.logIdOut4 ?? null,
        }));

        // Basit ozet hesapla
        const summary = {
          toplamPersonel: new Set(simplifiedRows.map((r: any) => r.kartNo)).size,
          devamEden: simplifiedRows.filter((r: any) => r.giris1).length,
          devamsiz: simplifiedRows.filter((r: any) => !r.giris1).length,
          cikisEksik: simplifiedRows.filter((r: any) => r.giris1 && !r.cikis1 && !r.cikis2 && !r.cikis3 && !r.cikis4).length
        };

        setReport({
          filter: data.filter,
          rows: simplifiedRows,
          summary,
          generatedAt: data.generatedAt
        });
      } else {
        console.error('Rapor yuklenemedi');
      }
    } catch (error) {
      console.error('Rapor yuklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const params = new URLSearchParams({
      startDate,
      endDate,
      durum
    });
    if (departmentId) params.append('departmentId', departmentId.toString());
    if (employeeId) params.append('employeeId', employeeId.toString());
    if (siteId !== 'all') params.append('siteId', siteId);
    if (locationId !== 'all') params.append('locationId', locationId);

    window.location.href = `${API_BASE}/export/excel?${params}`;
  };

  const exportToExcelPersonnel = () => {
    const params = new URLSearchParams({
      startDate,
      endDate,
      durum
    });
    if (departmentId) params.append('departmentId', departmentId.toString());
    if (employeeId) params.append('employeeId', employeeId.toString());
    if (siteId !== 'all') params.append('siteId', siteId);
    if (locationId !== 'all') params.append('locationId', locationId);

    window.location.href = `${API_BASE}/export/excel-personnel?${params}`;
  };

  const exportToPdf = () => {
    const params = new URLSearchParams({
      startDate,
      endDate,
      durum
    });
    if (departmentId) params.append('departmentId', departmentId.toString());
    if (employeeId) params.append('employeeId', employeeId.toString());
    if (siteId !== 'all') params.append('siteId', siteId);
    if (locationId !== 'all') params.append('locationId', locationId);

    window.location.href = `${API_BASE}/export/pdf?${params}`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '-';
    return timeStr.substring(0, 5);
  };

  // 2026-08-10: 4 giris/cikis cift edit — hangi cifti duzenleyecegini kullanici secer.
  // Her slot'un LogID'si (per-punch mode) veya EmployeeId+time (aggregate mode) ile deterministik update.
  const [editSlot, setEditSlot] = useState<1 | 2 | 3 | 4>(1);

  const handleEdit = (row: AttendanceLogRow) => {
    setEditingRow(row);
    // 2026-08-11: 4 cift default sec (Star Yapi gunluk 4 giris/cikis)
    let defaultSlot: 1 | 2 | 3 | 4 = 1;
    if (row.giris1 || row.cikis1) defaultSlot = 1;
    else if (row.giris2 || row.cikis2) defaultSlot = 2;
    else if (row.giris3 || row.cikis3) defaultSlot = 3;
    else if (row.giris4 || row.cikis4) defaultSlot = 4;
    setEditSlot(defaultSlot);
    setEditGiris(getSlotValue(row, defaultSlot, 'in'));
    setEditCikis(getSlotValue(row, defaultSlot, 'out'));
    setShowEditModal(true);

    // Cihazdan gelen yuz fotograflarini getir (varsa)
    setYuzKayitlari([]);
    const tarihStr = (typeof row.tarih === 'string')
      ? row.tarih.substring(0, 10)
      : new Date(row.tarih).toISOString().substring(0, 10);
    yuzKayitlariniYukle(row.kartNo, tarihStr);
  };

  const getSlotValue = (row: AttendanceLogRow, slot: 1|2|3|4, dir: 'in'|'out'): string => {
    const key = dir === 'in' ? `giris${slot}` : `cikis${slot}`;
    const v = (row as any)[key] as string | null;
    return v ? v.substring(0, 5) : '';
  };

  const onSlotChange = (slot: 1|2|3|4) => {
    if (!editingRow) return;
    // 2026-08-13: Onceki cift bos ise 2/3/4'e yazma — kullanici uyarisi
    if (slot > 1) {
      for (let i = 1; i < slot; i++) {
        const g = getSlotValue(editingRow, i as 1|2|3|4, 'in');
        const c = getSlotValue(editingRow, i as 1|2|3|4, 'out');
        if (!g && !c) {
          bildir({
            tur: 'uyari',
            baslik: 'Önce Önceki Çift Doldurulmalı',
            mesaj: `Çift ${slot} yazılabilmesi için Çift ${i} (giriş ve çıkış) dolu olmalı.`,
            notlar: ['Çiftler sırayla doldurulur; aradaki boş çift puantajı bozar.'],
          });
          return;
        }
      }
    }
    setEditSlot(slot);
    setEditGiris(getSlotValue(editingRow, slot, 'in'));
    setEditCikis(getSlotValue(editingRow, slot, 'out'));
  };

  // 2026-08-13: Kronolojik dogrulama — Cikis1 < Giris2 < Cikis2 < Giris3 ... sirali olmali
  const validateChronology = (): string | null => {
    if (!editingRow) return null;
    const toMin = (hm: string): number | null => {
      const m = /^(\d{1,2}):(\d{2})$/.exec(hm.trim());
      if (!m) return null;
      const h = +m[1], mi = +m[2];
      if (h > 23 || mi > 59) return null;
      return h * 60 + mi;
    };
    // Girilen degerleri format kontrolu
    if (editGiris && toMin(editGiris) === null) return `Giriş saati geçersiz: "${editGiris}" (HH:mm bekleniyor)`;
    if (editCikis && toMin(editCikis) === null) return `Çıkış saati geçersiz: "${editCikis}" (HH:mm bekleniyor)`;

    // 8 slot'u sirayla topla, duzenlenen slot'u yeni degerlerle degistir
    const seq: { label: string; min: number }[] = [];
    for (let s = 1 as 1|2|3|4; s <= 4; s++) {
      const gRaw = (s === editSlot) ? editGiris : getSlotValue(editingRow, s as 1|2|3|4, 'in');
      const cRaw = (s === editSlot) ? editCikis : getSlotValue(editingRow, s as 1|2|3|4, 'out');
      const g = gRaw ? toMin(gRaw) : null;
      const c = cRaw ? toMin(cRaw) : null;
      if (g !== null) seq.push({ label: `Giriş ${s}`, min: g });
      if (c !== null) seq.push({ label: `Çıkış ${s}`, min: c });
    }
    for (let i = 1; i < seq.length; i++) {
      if (seq[i].min < seq[i - 1].min) {
        const f = (n: number) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
        return `Saat sırası hatalı: ${seq[i].label} (${f(seq[i].min)}) — ${seq[i - 1].label} (${f(seq[i - 1].min)}) saatinden önce olamaz.`;
      }
      if (seq[i].min === seq[i - 1].min) {
        const f = (n: number) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
        return `${seq[i - 1].label} ve ${seq[i].label} aynı saatte olamaz (${f(seq[i].min)}).`;
      }
    }
    return null;
  };

  // Secili personel + tarih icin cihazdan gelen yuz fotograflarini getir
  const yuzKayitlariniYukle = async (kartNo: string, tarih: string) => {
    if (!kartNo || !tarih) { setYuzKayitlari([]); return; }
    setYuzYukleniyor(true);
    try {
      const g = tarih.substring(0, 10);
      const res = await fetch(
        `/api/hikvision/events-for-employee?employeeCode=${encodeURIComponent(kartNo)}&date=${g}`,
        { credentials: 'include' }
      );
      const json = await res.json();
      const liste = (json?.data ?? []).filter((e: any) => e.pictureUrl || e.savedPicturePath);
      setYuzKayitlari(liste);
    } catch {
      setYuzKayitlari([]);   // cihaz fotografi yoksa modal yine de calissin
    } finally {
      setYuzYukleniyor(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingRow) return;

    // Kronolojik dogrulama
    const chronoErr = validateChronology();
    if (chronoErr) {
      await bildir({ tur: 'tehlike', baslik: 'Saat Sırası Hatalı', mesaj: chronoErr });
      return;
    }

    // Gece vardiyasi uyarisi — vardiya gece yarisini asiyorsa erken sabah saatleri onceki gune yazilir
    const vStart = (editingRow as any).birim as string | undefined;   // VardiyaBaslangic
    const vEnd = (editingRow as any).firma as string | undefined;     // VardiyaBitis
    if (vStart && vEnd && vEnd < vStart) {
      const t = editGiris || editCikis;
      if (t && t < vEnd) {
        const ok = await sor({
          tur: 'uyari',
          baslik: 'Gece Vardiyası Uyarısı',
          mesaj: `Girdiğiniz saat (${t}) vardiya bitişinden (${vEnd}) önce olduğu için bu kayıt BİR ÖNCEKİ GÜNÜN vardiyasına ait sayılır ve o satırda görünür.`,
          satirlar: [{ etiket: 'Vardiya', deger: `${vStart} → ${vEnd}` }, { etiket: 'Girilen saat', deger: t }],
          onayMetni: 'Devam Et',
        });
        if (!ok) return;
      }
    }

    setSaving(true);
    try {
      // 2026-08-13 KRITIK: Backend row.tarih 'YYYY-MM-DDTHH:mm:ss' string; new Date() UTC parse edip
      // getDate() TR local'de 1 gun oncesine kayabiliyor (Test Personeli 13.08 gir/cik → 12.08 yaziliyordu).
      // Cozum: dateStr'i asla Date objesinden alma, string substring ile al.
      const dateStr = (typeof editingRow.tarih === 'string')
        ? editingRow.tarih.substring(0, 10)
        : new Date(editingRow.tarih).toISOString().substring(0, 10);
      const buildLocal = (hm: string) => hm ? `${dateStr}T${hm}:00` : null;
      const girisLocal = editGiris ? buildLocal(editGiris) : null;
      const cikisLocal = editCikis ? buildLocal(editCikis) : null;

      // Secili slot'un LogID'lerini al — LogId varsa direkt update, yoksa employeeId + tarih fallback
      const row = editingRow as any;
      const logIdIn = row[`logIdIn${editSlot}`] as number | null;
      const logIdOut = row[`logIdOut${editSlot}`] as number | null;

      // Ayni satirda giris ve cikis farkli AttendanceLog satirlarinda olabilir (per-punch mode).
      // İki ayri PUT çağrısı yap.
      const callUpdate = async (logId: number | null, giris: string | null, cikis: string | null) => {
        const res = await fetch(`${API_BASE}/update-times`, {
          method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            logId: logId ?? undefined,
            employeeId: editingRow.employeeId ?? undefined,
            kartNo: editingRow.kartNo,
            tarih: dateStr,
            giris, cikis
          })
        });
        return res.json();
      };

      // 2026-08-13: Cikis kaydedilmiyor bug'i — hem logIdIn hem logIdOut null iken (yeni cift ekleme)
      // 'logIdIn === logIdOut && null === null → true' oldugundan tek call yapiliyor + backend sadece giris'i
      // insert edip cikis'i yoksayiyordu. Fix: BOTH null olsa da 2 ayri call (per-punch).
      // 2026-08-14: SADECE DEGISEN alani gonder. Onceden her kaydetmede hem giris
      // hem cikis gonderiliyordu; degismeyen alan icin de yazma islemi tetikleniyor,
      // slot'un LogId'si yoksa YENI kayit ekleniyordu (saat bir sonraki bos slot'a
      // kayiyordu). Degismeyene dokunmayinca bu risk tamamen ortadan kalkiyor.
      const orjGiris = getSlotValue(editingRow, editSlot as 1|2|3|4, 'in') || '';
      const orjCikis = getSlotValue(editingRow, editSlot as 1|2|3|4, 'out') || '';
      const girisDegisti = (editGiris || '') !== orjGiris;
      const cikisDegisti = (editCikis || '') !== orjCikis;

      if (!girisDegisti && !cikisDegisti) {
        // Degisiklik yok — hata degil, sadece kapat.
        setShowEditModal(false);
        setEditingRow(null);
        return;
      }

      // 2026-08-14: Dolu bir alan BOSALTILDIYSA bu "sil" demektir.
      // Onceden hicbir istek gonderilmiyor, `result` undefined kaliyor ve
      // "Guncelleme basarisiz: {}" gibi anlamsiz bir hata cikiyordu.
      const callDelete = async (logId: number) => {
        const res = await fetch(`${API_BASE}/log/${logId}`, { method: 'DELETE', credentials: 'include' });
        try { return await res.json(); } catch { return { success: res.ok }; }
      };

      const hatalar: string[] = [];
      let islemYapildi = false;

      const sonucIsle = (r: any, ne: string) => {
        islemYapildi = true;
        if (!r?.success) {
          hatalar.push(`${ne}: ${r?.message || r?.title || 'bilinmeyen hata'}`);
        }
      };

      if (logIdIn && logIdOut && logIdIn === logIdOut && girisLocal && cikisLocal) {
        // Aggregate mod (eski kayit): tek log'da hem in hem out — tek call
        sonucIsle(await callUpdate(logIdIn, girisLocal, cikisLocal), 'Kayit');
      } else {
        // Per-punch: giris ayri, cikis ayri
        // KURAL: Giris bosaltildiysa o ciftin cikisi da silinir —
        // girisi olmayan cikis kaydi olamaz.
        const girisSiliniyor = girisDegisti && !girisLocal && !!logIdIn;

        if (girisSiliniyor && cikisLocal) {
          const onay = await sor({
            tur: 'tehlike',
            baslik: `Giriş ${editSlot} Siliniyor`,
            mesaj: 'Girişi olmayan bir çıkış kaydı olamaz; bu çiftin çıkış saati DE SİLİNECEK.',
            satirlar: [
              { etiket: 'Silinecek giriş', deger: orjGiris || '—' },
              { etiket: 'Silinecek çıkış', deger: editCikis || '—' },
            ],
            notlar: ['Personel silinmez, yalnızca bu okutma kayıtları kaldırılır.'],
            onayMetni: 'Çifti Sil',
          });
          if (!onay) return;
        }

        if (girisDegisti) {
          if (girisLocal) sonucIsle(await callUpdate(logIdIn, girisLocal, null), 'Giris');
          else if (logIdIn) sonucIsle(await callDelete(logIdIn), 'Giris silme');
          // logIdIn yok + deger de yok → zaten bostu, yapacak is yok
        }

        if (girisSiliniyor) {
          // Giris gittiyse cikis da gitmeli (kullanici zaten onayladi)
          if (logIdOut && logIdOut !== logIdIn) sonucIsle(await callDelete(logIdOut), 'Cikis silme');
        }
        else if (cikisDegisti) {
          if (cikisLocal) sonucIsle(await callUpdate(logIdOut, null, cikisLocal), 'Cikis');
          else if (logIdOut) sonucIsle(await callDelete(logIdOut), 'Cikis silme');
        }
      }

      if (hatalar.length > 0) {
        await bildir({
          tur: 'tehlike',
          baslik: 'Güncelleme Başarısız',
          mesaj: 'Aşağıdaki işlemler tamamlanamadı:',
          notlar: hatalar,
        });
        return;
      }

      // islemYapildi false ise (ornegin zaten silinmis bir alan) hata gosterme —
      // istenen son durum zaten saglanmis demektir.
      setShowEditModal(false);
      setEditingRow(null);
      loadReport();
    } catch (error: any) {
      await bildir({
        tur: 'tehlike',
        baslik: 'Güncelleme Hatası',
        mesaj: error?.message || 'Bilinmeyen bir hata oluştu.',
      });
    } finally {
      setSaving(false);
    }
  };

  // 2026-08-14 (kullanici talebi): Yanlis okutulmus SAATI silme.
  // Silinen sey personel DEGIL, yalnizca o okutma kaydidir (AttendanceLogs satiri).
  // hangi = 'giris' | 'cikis' — sadece o saat kaldirilir.
  // Ayni log satirinda hem giris hem cikis varsa (aggregate mod) satir silinmez,
  // yalnizca ilgili saat NULL'lanir; diger saat yerinde kalir.
  const handleDeleteSaat = async (hangi: 'giris' | 'cikis') => {
    if (!editingRow) return;

    const row = editingRow as any;
    const logIdIn = row[`logIdIn${editSlot}`] as number | null;
    const logIdOut = row[`logIdOut${editSlot}`] as number | null;
    const hedefId = hangi === 'giris' ? logIdIn : logIdOut;
    const digerId = hangi === 'giris' ? logIdOut : logIdIn;

    const saat = (hangi === 'giris'
      ? getSlotValue(editingRow, editSlot as 1|2|3|4, 'in')
      : getSlotValue(editingRow, editSlot as 1|2|3|4, 'out')) || '';

    if (!hedefId || hedefId <= 0) {
      await bildir({
        tur: 'uyari',
        baslik: saat ? 'Kayıt Bulunamadı' : 'Silinecek Saat Yok',
        mesaj: saat
          ? 'Bu saat henüz kaydedilmemiş görünüyor. Sayfayı yenileyip tekrar deneyin.'
          : `${hangi === 'giris' ? 'Giriş' : 'Çıkış'} ${editSlot} zaten boş.`,
      });
      return;
    }

    const etiket = hangi === 'giris' ? 'GİRİŞ' : 'ÇIKIŞ';
    const tarihStr = (typeof editingRow.tarih === 'string')
      ? editingRow.tarih.substring(0, 10)
      : new Date(editingRow.tarih).toISOString().substring(0, 10);

    // Ayni satirda iki saat birlikte tutuluyorsa satiri silmek digerini de goturur.
    const tekSatirda = !!digerId && digerId === hedefId;

    // 2026-08-14 KURAL (kullanici): "Giris olmazsa cikis da olmaz."
    // Girisi silerken o ciftin cikisi varsa O DA silinir. Onceden cikis
    // saati girise kayiyordu — sahada anlamsiz kayit uretiyordu.
    const cikisSaati = getSlotValue(editingRow, editSlot as 1|2|3|4, 'out') || '';
    const cikisDaSilinecek = hangi === 'giris' && !!cikisSaati;

    const onay = await sor({
      tur: 'tehlike',
      baslik: cikisDaSilinecek
        ? `Çift ${editSlot} Siliniyor`
        : `${etiket} ${editSlot} Saati Siliniyor`,
      mesaj: cikisDaSilinecek
        ? 'Girişi olmayan bir çıkış kaydı olamaz — bu çiftin çıkış saati de silinecek.'
        : hangi === 'cikis'
          ? 'Giriş saati yerinde kalacak, yalnızca çıkış kaldırılacak.'
          : 'Yalnızca bu okutma kaydı silinecek.',
      satirlar: [
        { etiket: 'Personel', deger: editingRow.isim || editingRow.kartNo },
        { etiket: 'Tarih', deger: tarihStr },
        { etiket: 'Silinecek saat', deger: saat || '(boş)' },
        ...(cikisDaSilinecek ? [{ etiket: 'Ayrıca silinecek çıkış', deger: cikisSaati }] : []),
      ],
      notlar: ['Personel silinmez, yalnızca okutma kaydı kaldırılır.', 'Puantaj yeniden hesaplanır.'],
      onayMetni: cikisDaSilinecek ? 'Çifti Sil' : 'Saati Sil',
    });
    if (!onay) return;

    setSaving(true);
    try {
      const sil = async (id: number, alan?: 'giris' | 'cikis') => {
        const url = alan
          ? `${API_BASE}/log/${id}/saat/${alan}`
          : `${API_BASE}/log/${id}`;
        const r = await fetch(url, { method: 'DELETE', credentials: 'include' });
        let j: any = null;
        try { j = await r.json(); } catch { /* bos govde */ }
        return { ok: r.ok && j?.success !== false, mesaj: j?.message || `HTTP ${r.status}` };
      };

      let sonucOk: boolean;
      let sonucMesaj: string;

      if (tekSatirda) {
        // Giris + cikis ayni satirda. Giris siliniyorsa backend satiri komple
        // kaldirir (cikis da gider); cikis siliniyorsa sadece cikis NULL'lanir.
        const r = await sil(hedefId, hangi);
        sonucOk = r.ok; sonucMesaj = r.mesaj;
      } else if (cikisDaSilinecek && digerId) {
        // Per-punch: giris ve cikis AYRI satirlar — ikisini de sil
        const r1 = await sil(digerId);     // once cikis
        const r2 = await sil(hedefId);     // sonra giris
        sonucOk = r1.ok && r2.ok;
        sonucMesaj = [r1.ok ? null : r1.mesaj, r2.ok ? null : r2.mesaj].filter(Boolean).join(' | ');
      } else {
        const r = await sil(hedefId);
        sonucOk = r.ok; sonucMesaj = r.mesaj;
      }

      if (!sonucOk) {
        await bildir({ tur: 'tehlike', baslik: 'Silme Başarısız', mesaj: sonucMesaj });
        return;
      }

      // Modal acik kalsin — kullanici digerini de silmek/duzenlemek isteyebilir.
      // editingRow'u da guncelle: aksi halde bayat kalir, ardindan Kaydet'e
      // basilinca "silinen alan degismis" sanilip bos istek uretiliyordu.
      if (hangi === 'giris') {
        setEditGiris('');
        // Giris silindiyse cikis da gitti — kutuyu da temizle
        if (cikisDaSilinecek || tekSatirda) setEditCikis('');
      } else {
        setEditCikis('');
      }

      setEditingRow(prev => {
        if (!prev) return prev;
        const kopya: any = { ...prev };
        if (hangi === 'giris') {
          // Giris silinince cift tamamen bosalir (cikis da silindi)
          kopya[`giris${editSlot}`] = null;
          kopya[`logIdIn${editSlot}`] = null;
          kopya[`cikis${editSlot}`] = null;
          kopya[`logIdOut${editSlot}`] = null;
        } else {
          kopya[`cikis${editSlot}`] = null;
          kopya[`logIdOut${editSlot}`] = null;
          // Ayni satirdaysa giris LogId'si de gecersizlesmis olabilir
          if (tekSatirda) kopya[`logIdIn${editSlot}`] = null;
        }
        return kopya;
      });
      loadReport();
    } catch (error: any) {
      await bildir({
        tur: 'tehlike',
        baslik: 'Silme Hatası',
        mesaj: error?.message || 'Bilinmeyen bir hata oluştu.',
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle sorting
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (key: SortKey) => {
    if (sortKey !== key) return <ArrowUpDown size={12} className="text-slate-300" />;
    return sortOrder === 'asc' ? <ArrowUp size={12} className="text-indigo-600" /> : <ArrowDown size={12} className="text-indigo-600" />;
  };

  const getFilteredAndSortedRows = useMemo((): AttendanceLogRow[] => {
    if (!report) return [];

    // First filter by status
    let filtered = report.rows;
    switch (activeFilter) {
      case 'present': filtered = filtered.filter(r => !!r.giris1); break;
      case 'absent': filtered = filtered.filter(r => !r.giris1); break;
      case 'exitMissing': filtered = filtered.filter(r => r.giris1 && !r.cikis1 && !r.cikis2 && !r.cikis3 && !r.cikis4); break;
    }

    // Then filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(r =>
        r.isim.toLowerCase().includes(term) ||
        r.kartNo.toLowerCase().includes(term) ||
        r.bolum?.toLowerCase().includes(term) ||
        r.grup?.toLowerCase().includes(term)
      );
    }

    // Finally sort
    const sorted = [...filtered].sort((a, b) => {
      let aVal: string | null = '';
      let bVal: string | null = '';

      switch (sortKey) {
        case 'kartNo': aVal = a.kartNo; bVal = b.kartNo; break;
        case 'isim': aVal = a.isim; bVal = b.isim; break;
        case 'bolum': aVal = a.bolum || ''; bVal = b.bolum || ''; break;
        case 'tarih': aVal = a.tarih; bVal = b.tarih; break;
        case 'giris1': aVal = a.giris1 || ''; bVal = b.giris1 || ''; break;
        case 'cikis1': aVal = a.cikis1 || ''; bVal = b.cikis1 || ''; break;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [report, activeFilter, searchTerm, sortKey, sortOrder]);

  // Keep old function for backward compatibility with summary count
  const getFilteredRows = (): AttendanceLogRow[] => {
    if (!report) return [];
    switch (activeFilter) {
      case 'present': return report.rows.filter(r => !!r.giris1);
      case 'absent': return report.rows.filter(r => !r.giris1);
      case 'exitMissing': return report.rows.filter(r => r.giris1 && !r.cikis1 && !r.cikis2 && !r.cikis3 && !r.cikis4);
      default: return report.rows;
    }
  };

  const getRowClass = (row: AttendanceLogRow) => {
    if (!row.giris1) return 'bg-red-50';
    if (row.giris1 && !row.cikis1) return 'bg-amber-50';
    return '';
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-3">
            <DoorOpen className="text-indigo-600" size={28} />
            {t('attendanceLogs.title')}
          </h1>
          <p className="text-slate-500 text-sm font-medium">{t('attendanceLogs.subtitle')}</p>
        </div>
        <div className="flex gap-3 w-full lg:w-auto">
          <button
            onClick={loadReport}
            disabled={loading}
            className="flex-1 lg:flex-none bg-indigo-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all active:scale-95 disabled:opacity-50"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
            {t('attendanceLogs.show')}
          </button>
          {report && report.rows.length > 0 && (
            <>
              <button
                onClick={exportToExcel}
                className="flex-1 lg:flex-none bg-green-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-green-700 shadow-xl shadow-green-100 transition-all active:scale-95"
              >
                <FileSpreadsheet size={18} /> Excel
              </button>
              <button
                onClick={exportToExcelPersonnel}
                className="flex-1 lg:flex-none bg-teal-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-teal-700 shadow-xl shadow-teal-100 transition-all active:scale-95"
                title={t('attendanceLogs.personnelExcelTooltip')}
              >
                <User size={18} /> {t('attendanceLogs.personnelExcel')}
              </button>
              <button
                onClick={exportToPdf}
                className="flex-1 lg:flex-none bg-red-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-red-700 shadow-xl shadow-red-100 transition-all active:scale-95"
              >
                <FileDown size={18} /> PDF
              </button>
            </>
          )}
        </div>
      </div>

      {/* Quick Date Filters */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 p-4">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs font-bold text-slate-500 mr-2">{t('attendanceLogs.quickSelect')}</span>
          <button
            onClick={() => {
              const today = new Date();
              setStartDate(today.toISOString().split('T')[0]);
              setEndDate(today.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('attendanceLogs.today')}
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const yesterday = new Date(today);
              yesterday.setDate(yesterday.getDate() - 1);
              setStartDate(yesterday.toISOString().split('T')[0]);
              setEndDate(yesterday.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('attendanceLogs.yesterday')}
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const last7 = new Date(today);
              last7.setDate(last7.getDate() - 6);
              setStartDate(last7.toISOString().split('T')[0]);
              setEndDate(today.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('attendanceLogs.last7Days')}
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const last15 = new Date(today);
              last15.setDate(last15.getDate() - 14);
              setStartDate(last15.toISOString().split('T')[0]);
              setEndDate(today.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('attendanceLogs.last15Days')}
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const last30 = new Date(today);
              last30.setDate(last30.getDate() - 29);
              setStartDate(last30.toISOString().split('T')[0]);
              setEndDate(today.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('attendanceLogs.last30Days')}
          </button>
          <div className="h-6 w-px bg-slate-200 mx-2" />
          <button
            onClick={() => {
              const today = new Date();
              const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
              setStartDate(firstDay.toISOString().split('T')[0]);
              setEndDate(today.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('attendanceLogs.thisMonth')}
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
              const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
              setStartDate(firstDay.toISOString().split('T')[0]);
              setEndDate(lastDay.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('attendanceLogs.lastMonth')}
          </button>
          <div className="h-6 w-px bg-slate-200 mx-2" />
          <select
            onChange={(e) => {
              if (e.target.value) {
                const monthIndex = parseInt(e.target.value);
                const today = new Date();
                const year = today.getFullYear();
                const firstDay = new Date(year, monthIndex, 1);
                const lastDay = new Date(year, monthIndex + 1, 0);
                setStartDate(firstDay.toISOString().split('T')[0]);
                setEndDate(lastDay.toISOString().split('T')[0]);
                e.target.value = '';
              }
            }}
            className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg text-xs font-bold transition-colors border-0 cursor-pointer"
            defaultValue=""
          >
            <option value="" disabled>{t('attendanceLogs.selectMonth')}</option>
            {MONTH_KEYS.map((key, idx) => (
              <option key={idx} value={idx}>{t(key)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 p-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5">{t('attendanceLogs.startDate')}</label>
            <div className="relative">
              <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5">{t('attendanceLogs.endDate')}</label>
            <div className="relative">
              <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5">{t('attendanceLogs.department')}</label>
            <div className="relative">
              <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={departmentId}
                onChange={(e) => { setDepartmentId(e.target.value ? Number(e.target.value) : ''); setEmployeeId(''); }}
                className="pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium appearance-none min-w-[200px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              >
                <option value="">{t('attendanceLogs.allDepartments')}</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5">{t('attendanceLogs.employee')}</label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value ? Number(e.target.value) : '')}
                className="pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium appearance-none min-w-[220px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              >
                <option value="">{t('attendanceLogs.allEmployees')}</option>
                {filteredEmployees.map(e => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5">{t('attendanceLogs.status')}</label>
            <div className="relative">
              <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={durum}
                onChange={(e) => setDurum(e.target.value)}
                className="pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium appearance-none min-w-[140px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              >
                <option value="Tumu">{t('attendanceLogs.statusAll')}</option>
                <option value="Aktif">{t('attendanceLogs.statusActive')}</option>
                <option value="Pasif">{t('attendanceLogs.statusInactive')}</option>
                {/* 2026-08-15 (kullanici): Backend 'IstenAyrilan' durumunu zaten
                    destekliyordu (TerminationDate IS NOT NULL) ama secenek yoktu. */}
                <option value="IstenAyrilan">{t('attendanceLogs.statusTerminated')}</option>
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          {/* Ag 2026: Şantiye + Lokasyon filtresi */}
          <div className="col-span-2">
            <label className="block text-xs font-bold text-slate-500 mb-1.5">Şantiye / Lokasyon</label>
            <SiteLocationFilter
              selectedSiteId={siteId}
              selectedLocationId={locationId}
              onSiteChange={setSiteId}
              onLocationChange={setLocationId}
            />
          </div>
        </div>
      </div>

      {/* Summary Cards - Clickable filters */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div
            className={`rounded-2xl p-5 cursor-pointer transition-all ${
              activeFilter === 'all'
                ? 'bg-slate-900 text-white ring-2 ring-blue-400'
                : 'bg-slate-900 text-white opacity-70 hover:opacity-100'
            }`}
            onClick={() => setActiveFilter('all')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                <User size={20} className="text-blue-400" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('attendanceLogs.totalEmployees')}</p>
                <h3 className="text-xl font-black">{report.summary.toplamPersonel}</h3>
              </div>
            </div>
          </div>
          <div
            className={`rounded-2xl p-5 cursor-pointer transition-all ${
              activeFilter === 'present'
                ? 'bg-green-600 text-white ring-2 ring-green-400 shadow-lg'
                : 'bg-white border border-slate-200 shadow-sm hover:shadow-md'
            }`}
            onClick={() => setActiveFilter(activeFilter === 'present' ? 'all' : 'present')}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${activeFilter === 'present' ? 'bg-white/20' : 'bg-green-50'}`}>
                <CheckCircle size={20} className={activeFilter === 'present' ? 'text-white' : 'text-green-600'} />
              </div>
              <div>
                <p className={`text-[10px] font-bold uppercase tracking-wider ${activeFilter === 'present' ? 'text-green-100' : 'text-slate-400'}`}>{t('attendanceLogs.checkedIn')}</p>
                <h3 className={`text-xl font-black ${activeFilter === 'present' ? 'text-white' : 'text-green-600'}`}>{report.summary.devamEden}</h3>
              </div>
            </div>
          </div>
          <div
            className={`rounded-2xl p-5 cursor-pointer transition-all ${
              activeFilter === 'absent'
                ? 'bg-red-600 text-white ring-2 ring-red-400 shadow-lg'
                : 'bg-white border border-slate-200 shadow-sm hover:shadow-md'
            }`}
            onClick={() => setActiveFilter(activeFilter === 'absent' ? 'all' : 'absent')}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${activeFilter === 'absent' ? 'bg-white/20' : 'bg-red-50'}`}>
                <XCircle size={20} className={activeFilter === 'absent' ? 'text-white' : 'text-red-600'} />
              </div>
              <div>
                <p className={`text-[10px] font-bold uppercase tracking-wider ${activeFilter === 'absent' ? 'text-red-100' : 'text-slate-400'}`}>{t('attendanceLogs.noEntry')}</p>
                <h3 className={`text-xl font-black ${activeFilter === 'absent' ? 'text-white' : 'text-red-600'}`}>{report.summary.devamsiz}</h3>
              </div>
            </div>
          </div>
          <div
            className={`rounded-2xl p-5 cursor-pointer transition-all ${
              activeFilter === 'exitMissing'
                ? 'bg-amber-500 text-white ring-2 ring-amber-400 shadow-lg'
                : 'bg-white border border-slate-200 shadow-sm hover:shadow-md'
            }`}
            onClick={() => setActiveFilter(activeFilter === 'exitMissing' ? 'all' : 'exitMissing')}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${activeFilter === 'exitMissing' ? 'bg-white/20' : 'bg-amber-50'}`}>
                <AlertTriangle size={20} className={activeFilter === 'exitMissing' ? 'text-white' : 'text-amber-600'} />
              </div>
              <div>
                <p className={`text-[10px] font-bold uppercase tracking-wider ${activeFilter === 'exitMissing' ? 'text-amber-100' : 'text-slate-400'}`}>{t('attendanceLogs.exitMissing')}</p>
                <h3 className={`text-xl font-black ${activeFilter === 'exitMissing' ? 'text-white' : 'text-amber-600'}`}>{report.summary.cikisEksik}</h3>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-8 py-5 border-b border-slate-100 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-black text-slate-800">
              {t('attendanceLogs.entryExitList')}
              {report && (() => {
                const filtered = getFilteredAndSortedRows;
                const filterLabel = activeFilter === 'present' ? t('attendanceLogs.filterCheckedIn') : activeFilter === 'absent' ? t('attendanceLogs.filterNoEntry') : activeFilter === 'exitMissing' ? t('attendanceLogs.filterExitMissing') : '';
                return <span className="text-slate-400 font-medium ml-2">({filtered.length} {t('attendanceLogs.records')}{filterLabel})</span>;
              })()}
            </h2>
            {activeFilter !== 'all' && (
              <button
                onClick={() => setActiveFilter('all')}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-800 px-3 py-1.5 bg-indigo-50 rounded-lg transition-colors"
              >
                {t('attendanceLogs.clearFilter')}
              </button>
            )}
          </div>
          <div className="relative w-full md:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder={t('attendanceLogs.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center">
            <Loader2 size={48} className="mx-auto text-indigo-500 animate-spin mb-4" />
            <p className="text-slate-400 font-bold">{t('attendanceLogs.loading')}</p>
          </div>
        ) : !report ? (
          <div className="py-20 text-center">
            <Calendar size={48} className="mx-auto text-slate-200 mb-4" />
            <h3 className="text-slate-400 font-bold uppercase text-xs tracking-wider">{t('attendanceLogs.selectDateRange')}</h3>
            <p className="text-slate-400 text-sm mt-2">{t('attendanceLogs.clickShowButton')}</p>
          </div>
        ) : report.rows.length === 0 ? (
          <div className="py-20 text-center">
            <AlertTriangle size={48} className="mx-auto text-slate-200 mb-4" />
            <h3 className="text-slate-400 font-bold uppercase text-xs tracking-wider">{t('attendanceLogs.noRecordsFound')}</h3>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[1000px]">
              <thead>
                <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50">
                  <th
                    className="py-4 px-4 sticky left-0 bg-indigo-100 text-indigo-800 cursor-pointer hover:bg-indigo-200 transition-colors"
                    onClick={() => handleSort('kartNo')}
                  >
                    <div className="flex items-center gap-1">{t('attendanceLogs.cardNo')} {getSortIcon('kartNo')}</div>
                  </th>
                  <th
                    className="py-4 px-4 bg-indigo-100 text-indigo-800 cursor-pointer hover:bg-indigo-200 transition-colors"
                    onClick={() => handleSort('isim')}
                  >
                    <div className="flex items-center gap-1">{t('attendanceLogs.name')} {getSortIcon('isim')}</div>
                  </th>
                  <th className="py-4 px-4">{t('attendanceLogs.group')}</th>
                  <th
                    className="py-4 px-4 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('bolum')}
                  >
                    <div className="flex items-center gap-1">{t('attendanceLogs.section')} {getSortIcon('bolum')}</div>
                  </th>
                  <th
                    className="py-4 px-4 bg-indigo-100 text-indigo-800 cursor-pointer hover:bg-indigo-200 transition-colors"
                    onClick={() => handleSort('tarih')}
                  >
                    <div className="flex items-center gap-1">{t('attendanceLogs.date')} {getSortIcon('tarih')}</div>
                  </th>
                  <th
                    className="py-4 px-4 bg-green-100 text-green-800 text-center cursor-pointer hover:bg-green-200 transition-colors"
                    onClick={() => handleSort('giris1')}
                  >
                    <div className="flex items-center justify-center gap-1">{t('attendanceLogs.entry1')} {getSortIcon('giris1')}</div>
                  </th>
                  <th
                    className="py-4 px-4 bg-blue-100 text-blue-800 text-center cursor-pointer hover:bg-blue-200 transition-colors"
                    onClick={() => handleSort('cikis1')}
                  >
                    <div className="flex items-center justify-center gap-1">{t('attendanceLogs.exit1')} {getSortIcon('cikis1')}</div>
                  </th>
                  <th className="py-4 px-4 text-center">{t('attendanceLogs.entry2')}</th>
                  <th className="py-4 px-4 text-center">{t('attendanceLogs.exit2')}</th>
                  <th className="py-4 px-4 text-center">{t('attendanceLogs.entry3')}</th>
                  <th className="py-4 px-4 text-center">{t('attendanceLogs.exit3')}</th>
                  <th className="py-4 px-4 text-center">GİRİŞ 4</th>
                  <th className="py-4 px-4 text-center">ÇIKIŞ 4</th>
                  {/* 2026-08-14 (kullanici talebi): fiili calisma suresi ekranda gorunsun */}
                  <th className="py-4 px-4 text-center bg-green-100 text-green-800">ÇALIŞMA</th>
                  <th className="py-4 px-4">{t('attendanceLogs.statusCol')}</th>
                  {canEditTimes && <th className="py-4 px-4 text-center">{t('attendanceLogs.action')}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {getFilteredAndSortedRows.map((row, idx) => (
                  <tr key={idx} className={`hover:bg-slate-50/50 ${getRowClass(row)}`}>
                    <td className="py-3 px-4 font-mono text-sm font-bold text-slate-700 sticky left-0 bg-inherit">{row.kartNo}</td>
                    <td className="py-3 px-4">
                      <span className="font-bold text-slate-800">{row.isim}</span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">{row.grup}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{row.bolum}</td>
                    <td className="py-3 px-4 text-sm font-medium">{formatDate(row.tarih)}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`font-mono font-bold ${row.giris1 ? 'text-green-600' : 'text-slate-300'}`}>
                        {formatTime(row.giris1)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`font-mono font-bold ${row.cikis1 ? 'text-blue-600' : 'text-slate-300'}`}>
                        {formatTime(row.cikis1)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center text-slate-500 font-mono text-sm">{formatTime(row.giris2)}</td>
                    <td className="py-3 px-4 text-center text-slate-500 font-mono text-sm">{formatTime(row.cikis2)}</td>
                    <td className="py-3 px-4 text-center text-slate-500 font-mono text-sm">{formatTime(row.giris3)}</td>
                    <td className="py-3 px-4 text-center text-slate-500 font-mono text-sm">{formatTime(row.cikis3)}</td>
                    <td className="py-3 px-4 text-center text-slate-500 font-mono text-sm">{formatTime(row.giris4)}</td>
                    <td className="py-3 px-4 text-center text-slate-500 font-mono text-sm">{formatTime(row.cikis4)}</td>
                    {/* 2026-08-14: O gun fiilen calisilan sure */}
                    <td className="py-3 px-4 text-center bg-green-50/50">
                      {row.normalCalisma && row.normalCalisma !== '00:00:00' ? (
                        <span className="font-bold text-green-700 font-mono text-sm">
                          {String(row.normalCalisma).substring(0, 5)}
                        </span>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {!row.giris1 ? (
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded-lg text-xs font-bold">{t('attendanceLogs.statusNoEntry')}</span>
                      ) : !row.cikis1 ? (
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-lg text-xs font-bold">{t('attendanceLogs.statusExitMissing')}</span>
                      ) : (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-bold">{t('attendanceLogs.statusOk')}</span>
                      )}
                    </td>
                    {canEditTimes && (
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => handleEdit(row)}
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                          title={t('attendanceLogs.edit')}
                        >
                          <Edit2 size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingRow && (
        <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-black text-slate-800">{t('attendanceLogs.editTitle')}</h3>
              <button
                onClick={() => { setShowEditModal(false); setEditingRow(null); }}
                className="p-2 hover:bg-slate-100 rounded-full transition-all"
              >
                <X size={20} className="text-slate-400" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{t('attendanceLogs.employeeLabel')}</p>
                <p className="font-bold text-slate-800">{editingRow.isim}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{t('attendanceLogs.cardNoLabel')}</p>
                  <p className="font-mono font-bold text-slate-800">{editingRow.kartNo}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{t('attendanceLogs.dateLabel')}</p>
                  <p className="font-bold text-slate-800">{formatDate(editingRow.tarih)}</p>
                </div>
              </div>

              {/* 2026-08-11: 4 cift slot secici (Star Yapi: gunluk 4 giris/cikis) */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Hangi Giriş/Çıkış Çifti?</label>
                <div className="grid grid-cols-4 gap-2">
                  {([1, 2, 3, 4] as const).map(slot => {
                    const gir = getSlotValue(editingRow, slot, 'in');
                    const cik = getSlotValue(editingRow, slot, 'out');
                    const empty = !gir && !cik;
                    const active = editSlot === slot;
                    return (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => onSlotChange(slot)}
                        className={`p-2 rounded-lg border text-center transition-all ${
                          active
                            ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                            : empty
                              ? 'border-dashed border-slate-200 bg-white text-slate-400 hover:border-slate-300'
                              : 'border-slate-200 bg-white hover:border-indigo-300'
                        }`}
                      >
                        <p className={`text-[10px] font-black uppercase ${active ? 'text-indigo-700' : 'text-slate-500'}`}>Çift {slot}</p>
                        <p className={`text-xs font-mono mt-1 ${empty ? 'text-slate-300' : 'text-slate-700'}`}>
                          {gir || '--:--'} / {cik || '--:--'}
                        </p>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Boş çiftler yeni giriş/çıkış eklemek için kullanılabilir.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                    <Clock size={12} className="inline mr-1 text-green-600" />
                    {t('attendanceLogs.entryTime')} {editSlot}
                    {/* 2026-08-14: Yanlis okutulmus SAATI sil (personeli degil) */}
                    {!!(editingRow as any)?.[`logIdIn${editSlot}`] && (
                      <button
                        type="button"
                        onClick={() => handleDeleteSaat('giris')}
                        disabled={saving}
                        title={
                          getSlotValue(editingRow!, editSlot as 1|2|3|4, 'out')
                            ? 'Girişi sil — çıkış da silinir (girişsiz çıkış olamaz)'
                            : 'Bu giriş saatini sil'
                        }
                        className="float-right normal-case tracking-normal text-[11px] font-bold text-red-600 hover:text-red-700 hover:underline disabled:opacity-40 flex items-center gap-1"
                      >
                        <Trash2 size={12} />
                        {getSlotValue(editingRow!, editSlot as 1|2|3|4, 'out') ? 'Çifti Sil' : 'Saati Sil'}
                      </button>
                    )}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={editGiris}
                    onChange={(e) => {
                      // 24 saat HH:mm — AM/PM karisikligini onlemek icin native time input yerine text
                      let v = e.target.value.replace(/[^0-9:]/g, '').substring(0, 5);
                      if (v.length === 2 && !v.includes(':') && editGiris.length !== 3) v += ':';
                      setEditGiris(v);
                    }}
                    placeholder="HH:mm"
                    maxLength={5}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 font-mono text-lg tracking-wider"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                    <Clock size={12} className="inline mr-1 text-blue-600" />
                    {t('attendanceLogs.exitTime')} {editSlot}
                    {/* 2026-08-14: Yanlis okutulmus SAATI sil (personeli degil) */}
                    {!!(editingRow as any)?.[`logIdOut${editSlot}`] && (
                      <button
                        type="button"
                        onClick={() => handleDeleteSaat('cikis')}
                        disabled={saving}
                        title="Sadece çıkışı sil — giriş yerinde kalır"
                        className="float-right normal-case tracking-normal text-[11px] font-bold text-red-600 hover:text-red-700 hover:underline disabled:opacity-40 flex items-center gap-1"
                      >
                        <Trash2 size={12} /> Saati Sil
                      </button>
                    )}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={editCikis}
                    onChange={(e) => {
                      let v = e.target.value.replace(/[^0-9:]/g, '').substring(0, 5);
                      if (v.length === 2 && !v.includes(':') && editCikis.length !== 3) v += ':';
                      setEditCikis(v);
                    }}
                    placeholder="HH:mm"
                    maxLength={5}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-lg tracking-wider"
                  />
                </div>
              </div>
            </div>

            {/* 2026-08-15 (kullanici talebi): Girilen saatlerden CANLI hesaplanan
                calisma suresi. Onceden modalda hic gosterilmiyordu; kullanici
                kaydetmeden kac saat oldugunu goremiyordu. */}
            {(() => {
              const dk = (hm: string) => {
                const m = /^(\d{1,2}):(\d{2})$/.exec(hm || '');
                return m ? Number(m[1]) * 60 + Number(m[2]) : null;
              };
              const g = dk(editGiris), c = dk(editCikis);
              if (g === null && c === null) return null;

              // Bu ciftin disindaki dolu ciftleri de topla — gunun toplami gorunsun
              let toplam = 0, eksikVar = false;
              for (const sl of [1, 2, 3, 4] as const) {
                const gi = sl === editSlot ? editGiris : getSlotValue(editingRow!, sl, 'in');
                const ci = sl === editSlot ? editCikis : getSlotValue(editingRow!, sl, 'out');
                const a = dk(gi || ''), b = dk(ci || '');
                if (a !== null && b !== null) {
                  let f = b - a;
                  if (f < 0) f += 24 * 60;      // gece vardiyasi
                  toplam += f;
                } else if (a !== null || b !== null) {
                  eksikVar = true;
                }
              }

              let buCift: number | null = null;
              if (g !== null && c !== null) {
                buCift = c - g;
                if (buCift < 0) buCift += 24 * 60;
              }
              const bicim = (m: number) => `${Math.floor(m / 60)} sa ${m % 60} dk`;

              return (
                <div className="mb-5 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[11px] font-black uppercase tracking-wider text-green-700">
                        Bu Çift (Giriş/Çıkış {editSlot})
                      </div>
                      <div className="text-lg font-black text-green-800 mt-0.5">
                        {buCift !== null ? bicim(buCift) : '— (giriş veya çıkış eksik)'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] font-black uppercase tracking-wider text-slate-500">
                        Günün Toplamı
                      </div>
                      <div className="text-lg font-black text-slate-800 mt-0.5">{bicim(toplam)}</div>
                    </div>
                  </div>
                  {eksikVar && (
                    <div className="mt-2 text-[11px] text-amber-700">
                      Bazı çiftlerde giriş veya çıkış eksik — o çiftler toplama katılmadı.
                    </div>
                  )}
                  <div className="mt-1 text-[11px] text-slate-500">
                    Mola, puantaj hesaplanırken vardiya ayarına göre ayrıca düşülür.
                  </div>
                </div>
              );
            })()}

            {/* 2026-08-14 (kullanici): Cihazda yuz okutulduysa cekilen fotograf.
                Veritabaninda tutuluyordu ama arayuzde hicbir yerde gosterilmiyordu. */}
            {(yuzYukleniyor || yuzKayitlari.length > 0) && (
              <div className="mb-5 border border-slate-200 rounded-xl p-3 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-black uppercase tracking-wider text-slate-500">
                    Cihaz Yüz Kayıtları
                  </span>
                  {!yuzYukleniyor && (
                    <span className="text-[11px] text-slate-400">{yuzKayitlari.length} fotoğraf</span>
                  )}
                </div>

                {yuzYukleniyor ? (
                  <div className="flex items-center gap-2 text-sm text-slate-400 py-3">
                    <Loader2 size={14} className="animate-spin" /> Yükleniyor...
                  </div>
                ) : (
                  <div className="flex gap-3 overflow-x-auto pb-1">
                    {yuzKayitlari.map((k: any) => {
                      const url = k.pictureUrl || k.savedPicturePath;
                      const saat = k.eventTime ? String(k.eventTime).substring(11, 16) : '--:--';
                      return (
                        <button
                          key={k.id}
                          type="button"
                          onClick={() => setBuyukFoto(url)}
                          title="Büyütmek için tıklayın"
                          className="shrink-0 text-center group"
                        >
                          <img
                            src={url}
                            alt={saat}
                            loading="lazy"
                            className="w-16 h-16 object-cover rounded-lg border-2 border-slate-200 group-hover:border-indigo-400 transition-all"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                          />
                          <div className="text-[11px] font-mono font-bold text-slate-600 mt-1">{saat}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={() => { setShowEditModal(false); setEditingRow(null); }}
                className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-all"
              >
                {t('attendanceLogs.cancel')}
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {saving ? t('attendanceLogs.saving') : t('attendanceLogs.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    {/* 2026-08-15 (kullanici talebi): Tarayicinin standart siyah confirm/alert
        kutusu yerine uygulama ici, renk kodlu ve okunakli modal. */}
      {onayKutusu && (() => {
        const renk = {
          onay:     { kenar: 'border-blue-200',   bas: 'bg-blue-600',   ikon: 'text-blue-600',   dugme: 'bg-blue-600 hover:bg-blue-700' },
          uyari:    { kenar: 'border-amber-200',  bas: 'bg-amber-500',  ikon: 'text-amber-600',  dugme: 'bg-amber-600 hover:bg-amber-700' },
          tehlike:  { kenar: 'border-red-200',    bas: 'bg-red-600',    ikon: 'text-red-600',    dugme: 'bg-red-600 hover:bg-red-700' },
          bilgi:    { kenar: 'border-slate-200',  bas: 'bg-slate-700',  ikon: 'text-slate-600',  dugme: 'bg-slate-700 hover:bg-slate-800' },
        }[onayKutusu.tur];

        const kapat = (kabul: boolean) => { onayKutusu.cevapla(kabul); setOnayKutusu(null); };

        return (
          <div
            className="fixed inset-0 z-[70] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => kapat(false)}
          >
            <div
              className={`bg-white rounded-2xl shadow-2xl border ${renk.kenar} w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-150`}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className={`${renk.bas} px-5 py-4 flex items-center gap-3`}>
                <AlertTriangle size={20} className="text-white shrink-0" />
                <h3 className="text-white font-black text-base">{onayKutusu.baslik}</h3>
              </div>

              <div className="p-5">
                <p className="text-sm text-slate-700 leading-relaxed">{onayKutusu.mesaj}</p>

                {onayKutusu.satirlar && onayKutusu.satirlar.length > 0 && (
                  <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl divide-y divide-slate-100">
                    {onayKutusu.satirlar.map((r, i) => (
                      <div key={i} className="flex justify-between gap-3 px-3 py-2 text-sm">
                        <span className="text-slate-500">{r.etiket}</span>
                        <span className="font-bold text-slate-800 font-mono">{r.deger}</span>
                      </div>
                    ))}
                  </div>
                )}

                {onayKutusu.notlar && onayKutusu.notlar.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {onayKutusu.notlar.map((n, i) => (
                      <li key={i} className="text-xs text-slate-500 flex gap-2">
                        <span className={renk.ikon}>•</span>
                        <span>{n}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="px-5 pb-5 flex gap-3">
                {!onayKutusu.tekButon && (
                  <button
                    onClick={() => kapat(false)}
                    className="flex-1 py-2.5 border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50 transition-all"
                  >
                    Vazgeç
                  </button>
                )}
                <button
                  autoFocus
                  onClick={() => kapat(true)}
                  className={`flex-1 py-2.5 ${renk.dugme} text-white rounded-xl font-bold transition-all`}
                >
                  {onayKutusu.onayMetni ?? (onayKutusu.tekButon ? 'Tamam' : 'Devam Et')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    {/* Fotograf buyutme */}
      {buyukFoto && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6"
          onClick={() => setBuyukFoto(null)}
        >
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={buyukFoto}
              alt="Yüz kaydı"
              className="max-h-[80vh] max-w-[90vw] rounded-2xl shadow-2xl"
            />
            <button
              onClick={() => setBuyukFoto(null)}
              className="absolute -top-3 -right-3 w-9 h-9 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-100"
              title="Kapat"
            >
              <X size={18} className="text-slate-600" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceLogs;
